import { useState, useEffect, useRef } from 'react'
import { Box, Typography, Button, Alert } from '@mui/material'
import DoodleSpinner from './DoodleSpinner'
import { COLORS } from '../theme'
import { primaryButtonSx, outlinedButtonSx } from '../styles'

// ── Carousel animation constants ──────────────────────────────────────
// Controls the timing of help text transitions in the carousel below the status text.
const HELP_TEXT_FADE_DURATION_MS = 300  // Duration of fade in/out transition

interface ProgressScreenProps {
  statusText: string
  helpTexts: string[]
  rotateInterval?: number
  /** Optional 0–100 progress percentage shown below the spinner */
  progress?: number
  error?: string | null
  onRetry?: () => void
  onBack?: () => void
  onSkip?: () => void
  skipLabel?: string
}

export default function ProgressScreen({
  statusText,
  helpTexts,
  rotateInterval = 6000,
  progress,
  error,
  onRetry,
  onBack,
  onSkip,
  skipLabel = 'Skip',
}: ProgressScreenProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [fade, setFade] = useState(true)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (helpTexts.length <= 1) return

    timerRef.current = setInterval(() => {
      setFade(false)
      setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % helpTexts.length)
        setFade(true)
      }, HELP_TEXT_FADE_DURATION_MS)
    }, rotateInterval)

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [helpTexts.length, rotateInterval])

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 4,
          textAlign: 'center',
        }}
      >
        <Alert severity="error" sx={{ mb: 3, maxWidth: 480 }}>
          Sorry, something went wrong. Please try again.
        </Alert>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {onBack && (
            <Button
              disableRipple
              variant="outlined"
              onClick={onBack}
              sx={outlinedButtonSx}
            >
              Go back
            </Button>
          )}
          {onRetry && (
            <Button
              disableRipple
              variant="contained"
              onClick={onRetry}
              sx={primaryButtonSx}
            >
              Try again
            </Button>
          )}
          {onSkip && (
            <Button
              disableRipple
              variant="outlined"
              onClick={onSkip}
              sx={outlinedButtonSx}
            >
              {skipLabel}
            </Button>
          )}
        </Box>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        px: 4,
        textAlign: 'center',
      }}
    >
      <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <DoodleSpinner />
        <Typography
          variant="body2"
          role="progressbar"
          aria-valuenow={progress !== undefined ? Math.round(progress) : undefined}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Loading progress"
          sx={{
            color: COLORS.primary,
            fontWeight: 600,
            fontSize: '0.9rem',
            opacity: 0.7,
            display: progress !== undefined ? 'block' : 'none',
          }}
        >
          {progress !== undefined ? `${Math.round(progress)}%` : ''}
        </Typography>
      </Box>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 500,
          color: COLORS.primary,
          mb: '4rem',
        }}
      >
        {statusText}
      </Typography>
      {/* Carousel text — fixed height so swapping text never causes layout shift */}
      <Box sx={{ width: '100%', maxWidth: 480, height: 80, position: 'relative' }}>
        <Typography
          variant="body2"
          sx={{
            color: COLORS.secondary,
            lineHeight: 1.6,
            transition: 'opacity 0.3s ease',
            opacity: fade ? 1 : 0,
            position: 'absolute',
            inset: 0,
          }}
        >
          {helpTexts[activeIndex]}
        </Typography>
      </Box>
    </Box>
  )
}
